import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface Heading {
  id: string;
  text: string;
  level: number;
}

const TableOfContents = () => {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    // We add a slight delay to ensure MDX has rendered
    const timer = setTimeout(() => {
      const elements = Array.from(document.querySelectorAll('article h2, article h3'));
      
      const headingData = elements.map((elem) => {
        // Ensure the element has an ID
        if (!elem.id) {
          elem.id = elem.textContent?.toLowerCase().replace(/\\s+/g, '-').replace(/[^a-z0-9-]/g, '') || '';
        }
        
        return {
          id: elem.id,
          text: elem.textContent || '',
          level: Number(elem.tagName.substring(1))
        };
      });
      
      setHeadings(headingData);

      // Setup Intersection Observer for Scroll Spy
      const observerCallback = (entries: IntersectionObserverEntry[]) => {
        // Find the first heading that is currently intersecting the viewport
        // or the most recently passed one
        const intersecting = entries.find((entry) => entry.isIntersecting);
        if (intersecting) {
          setActiveId(intersecting.target.id);
        }
      };

      const observer = new IntersectionObserver(observerCallback, {
        rootMargin: '-100px 0px -66% 0px', // Trigger when heading is near the top
      });

      elements.forEach((elem) => observer.observe(elem));

      return () => {
        elements.forEach((elem) => observer.unobserve(elem));
      };
    }, 100); // Wait for render

    return () => clearTimeout(timer);
  }, []);

  if (headings.length === 0) return null;

  return (
    <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto custom-scrollbar pr-2">
      <h4 className="text-sm font-semibold tracking-wider text-muted-foreground uppercase mb-4">
        On this page
      </h4>
      <nav className="flex flex-col space-y-2.5">
        {headings.map((heading) => (
          <a
            key={heading.id}
            href={`#${heading.id}`}
            className={cn(
              "text-sm transition-colors hover:text-foreground line-clamp-2",
              heading.level === 3 ? "pl-4" : "",
              activeId === heading.id 
                ? "text-primary font-medium" 
                : "text-muted-foreground"
            )}
            onClick={(e) => {
              e.preventDefault();
              document.querySelector(`#${heading.id}`)?.scrollIntoView({
                behavior: 'smooth'
              });
              // Update URL without jump
              window.history.pushState(null, '', `#${heading.id}`);
            }}
          >
            {heading.text}
          </a>
        ))}
      </nav>
    </div>
  );
};

export default TableOfContents;
